import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { LuCircleCheck } from "react-icons/lu";
import { Brand, Note } from "../components/Ui";

const ENDPOINT = "https://us-east1-foursssolutions.cloudfunctions.net/send_activation_link_v2";

export default function Activate() {
  const location = useLocation();
  const navigate = useNavigate();
  const apiToken = useSelector((s) => s.profile.apiToken);

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { kind: "success" | "expired" | "failed" | "error", message }
  const [countdown, setCountdown] = useState(5);
  const intervalRef = useRef(null);
  const delayRef = useRef(null);

  /* extract the activation token from the URL query string (same as legacy) */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    setToken(queryParams.get("token"));
  }, [location.search]);

  /* clear any pending countdown / delayed handler on unmount */
  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (delayRef.current) clearTimeout(delayRef.current);
    },
    []
  );

  function startCountdown(target) {
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(intervalRef.current);
          navigate(target);
        }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleActivate() {
    setLoading(true);
    setResult(null);
    try {
      /* same endpoint, headers and payload as legacy */
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          token: token,
          task: "verify_token",
        }),
      });
      const data = await response.json();

      /* legacy handled the result behind a 4-second delay — same branching */
      delayRef.current = setTimeout(() => {
        setLoading(false);
        if (response.ok && data.status !== "token not valid or expired") {
          setResult({ kind: "success" });
          startCountdown("/login");
        } else if (data.status === "token not valid or expired") {
          setResult({ kind: "expired" });
          startCountdown("/reactivate-link");
        } else {
          setResult({ kind: "failed", message: `Verification failed: ${data.status}` });
        }
      }, 4000);
    } catch (err) {
      setLoading(false);
      setResult({
        kind: "error",
        message: "An error occurred while verifying your account. Please try again.",
      });
    }
  }

  const redirecting = result && (result.kind === "success" || result.kind === "expired");

  return (
    <div className="csx">
      <div className="csx-page">
        <div className="auth-main" style={{ flex: 1 }}>
          <Brand />
          <div className="auth-card stack-3">
            <div className="stack-2">
              <h1 className="t-h1">Verify your profile</h1>
              <p className="t-sm">
                To activate your profile, click the button below. This will verify your account and allow you
                to access the platform.
              </p>
            </div>

            {result && result.kind === "success" ? (
              <Note tone="accent">
                <LuCircleCheck size={17} />
                <span>
                  Your account is now activated. You can sign in with your credentials. Redirecting to{" "}
                  <Link to="/login" style={{ fontWeight: 600, textDecoration: "underline", color: "inherit" }}>
                    sign in
                  </Link>{" "}
                  in {countdown} seconds.
                </span>
              </Note>
            ) : null}
            {result && result.kind === "expired" ? (
              <Note tone="amber">
                <span>
                  Your activation link has expired. You can request a new one. Redirecting to the{" "}
                  <Link
                    to="/reactivate-link"
                    style={{ fontWeight: 600, textDecoration: "underline", color: "inherit" }}
                  >
                    resend activation page
                  </Link>{" "}
                  in {countdown} seconds.
                </span>
              </Note>
            ) : null}
            {result && (result.kind === "failed" || result.kind === "error") ? (
              <Note tone="danger">
                <span>{result.message} You can try again below.</span>
              </Note>
            ) : null}

            <button
              type="button"
              className="btn btn-primary btn-lg btn-full"
              onClick={handleActivate}
              disabled={loading || redirecting}
            >
              {loading ? <span className="spinner" /> : null}
              Activate profile
            </button>

            <p className="t-sm t-center">
              <Link to="/login" className="t-accent" style={{ fontWeight: 600 }}>
                Go to sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
